import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import React from 'react';

import * as api from '../../api.js';

import { DateTime } from 'luxon';

import { useAuth } from '../../AuthContext';
import { Filter } from '../Filter/Filter';
import { ShiftsHeader } from './ShiftsHeader';
import { ShiftsList } from './ShiftsList';
import { ShiftsFooter } from './ShiftsFooter';
import { ShiftAddDialog } from './ShiftAddDialog';
import { IntegrityCheckDialog } from '../IntegrityCheckDialog/IntegrityCheckDialog';
import { integrityCheckService } from '../../services/integrityChecks';
import { advanceSeriesDate } from '../../services/seriesDates';

const isNewShift = (id) => /^new-\d+(-\d+)?$/.test(id);

/**
 * Simulates series creation to generate preview shifts for integrity checking
 */
export const simulateSeriesCreation = (shift, event) => {
    const seriesShifts = [];
    const start = new Date(shift.date);
    const end = new Date(event.end);

    let current = new Date(start);

    while (current <= end && [ 'daily', 'weekly', 'monthly' ].includes(shift.recurrence)) {
        const isoDate = current.toISOString().split('T')[ 0 ];

        // Convert time to epoch milliseconds for the new date
        const timeObj = DateTime.fromMillis(shift.start, { zone: shift.timezone || 'UTC' });
        const newStartTime = DateTime.fromObject(
            {
                year: Number(isoDate.slice(0, 4)),
                month: Number(isoDate.slice(5, 7)),
                day: Number(isoDate.slice(8, 10)),
                hour: timeObj.hour,
                minute: timeObj.minute,
            }, { zone: shift.timezone || 'UTC' }
        ).toMillis();

        const timeObjEnd = DateTime.fromMillis(shift.end, { zone: shift.timezone || 'UTC' });
        const newEndTime = DateTime.fromObject(
            {
                year: Number(isoDate.slice(0, 4)),
                month: Number(isoDate.slice(5, 7)),
                day: Number(isoDate.slice(8, 10)),
                hour: timeObjEnd.hour,
                minute: timeObjEnd.minute,
            }, { zone: shift.timezone || 'UTC' }
        ).toMillis();

        seriesShifts.push({
            name: shift.name,
            date: isoDate,
            start: newStartTime,
            end: newEndTime,
            event: event.id,
            recurrence: shift.recurrence,
            timezone: shift.timezone || 'UTC',
            slots: shift.slots
        });

        advanceSeriesDate(current, shift.recurrence);
    }

    return seriesShifts;
};

export const Shifts = ({ event, roles, readOnly, size = 10 }) => {
    const [ error, setError ] = React.useState(null);
    const [ loading, setLoading ] = React.useState(true);
    const [ shifts, setShifts ] = React.useState([]);
    const [ selectedShifts, setSelectedShifts ] = React.useState([]);
    const [ addShifts, setAddShifts ] = React.useState(false);
    const [ undo, setUndo ] = React.useState([]);
    const [ copyDialogOpen, setCopyDialogOpen ] = React.useState(false);
    const [ copyOffset, setCopyOffset ] = React.useState(0);
    const [ integrityDialogOpen, setIntegrityDialogOpen ] = React.useState(false);
    const [ integrityResult, setIntegrityResult ] = React.useState(null);
    const [ shiftIntegrityIssues, setShiftIntegrityIssues ] = React.useState([]);
    const [ pendingShifts, setPendingShifts ] = React.useState([]);
    const [ pendingSeriesShift, setPendingSeriesShift ] = React.useState(null);
    const [ patrollers, setPatrollers ] = React.useState([]);
    const [ newShiftDialogOpen, setNewShiftDialogOpen ] = React.useState(false);
    const [ newShiftTemplate, setNewShiftTemplate ] = React.useState(null);

    const memoizedPatrollers = React.useMemo(() => {
        return (patrollers || []).map(patroller => ({
            id: patroller.id,
            name: `${ patroller.lastName }, ${ patroller.firstName?.charAt(0) }`
        }));
    }, [ patrollers ]);

    const checkExistingShiftsIntegrity = async (shifts) => {
        if (!shifts || shifts.length === 0) {
            setShiftIntegrityIssues([]);
            return;
        }

        // Check for duplicates among existing shifts
        const duplicateIssues = [];

        for (let i = 0; i < shifts.length; i++) {
            const currentShift = shifts[ i ];
            const duplicates = shifts.slice(i + 1).filter(other =>
                isDuplicateShift(currentShift, other)
            );

            if (duplicates.length > 0) {
                duplicateIssues.push({
                    shiftId: currentShift.id,
                    type: 'duplicate',
                    severity: 'warning',
                    message: `Duplicate shift: "${ currentShift.name }" conflicts with ${ duplicates.length } other shift(s)`
                });
            }
        }

        setShiftIntegrityIssues(duplicateIssues);
    };

    // Helper function to check if two shifts are duplicates (extracted from integrityChecks.js)
    const isDuplicateShift = (shift1, shift2) => {
        // Must have same name, date, start time, end time
        if (shift1.name !== shift2.name) return false;
        if (shift1.date !== shift2.date) return false;
        if (shift1.start !== shift2.start) return false;
        if (shift1.end !== shift2.end) return false;

        // Must have same slot configuration (roles and counts)
        return areSlotsEqual(shift1.slots, shift2.slots);
    };

    // Helper function to compare slot configurations
    const areSlotsEqual = (slots1, slots2) => {
        if (!slots1 || !slots2) return slots1 === slots2;
        if (slots1.length !== slots2.length) return false;

        // Sort slots by role for consistent comparison
        const sorted1 = [ ...slots1 ].sort((a, b) => (a.role || '').localeCompare(b.role || ''));
        const sorted2 = [ ...slots2 ].sort((a, b) => (a.role || '').localeCompare(b.role || ''));

        return sorted1.every((slot, index) =>
            slot.role === sorted2[ index ].role &&
            slot.count === sorted2[ index ].count
        );
    };

    const [ page, setPage ] = React.useState(1);
    const [ pageSize, setPageSize ] = React.useState(25);
    const [ filters, setFilters ] = React.useState({
        search: '',
        excludeSearchParams: false,
        days: [],
        startDate: null,
        endDate: null,
        onlyAvailable: false
    });

    const { token } = useAuth();

    const filteredShifts = React.useMemo(() => {
        const sortedShifts = [ ...shifts ].sort((a, b) => {
            if (a.start && b.start) return a.start - b.start;
            if (a.start) return -1;
            if (b.start) return 1;
            return 0;
        });

        return sortedShifts.filter(shift => {
            let include = true;

            // Search filter (name, event, or time)
            if (filters.search) {
                const searchLower = filters.search.toLowerCase();

                // Check shift name
                const matchesName = shift.name?.toLowerCase().includes(searchLower);

                // Check shift time
                const shiftStartTime = DateTime.fromMillis(shift.start).toFormat('HH:mm');
                const shiftEndTime = DateTime.fromMillis(shift.end).toFormat('HH:mm');
                const matchesTime = shiftStartTime.includes(searchLower) || shiftEndTime.includes(searchLower);

                // Check shift date (full date, short date, or month)
                const shiftDateFull = DateTime.fromMillis(shift.start).toFormat('MMMM dd, yyyy').toLowerCase();
                const shiftDateShort = DateTime.fromMillis(shift.start).toFormat('MM/dd/yyyy').toLowerCase();
                const shiftDateMonth = DateTime.fromMillis(shift.start).toFormat('MMMM').toLowerCase();
                const matchesDate = shiftDateFull.includes(searchLower) || shiftDateShort.includes(searchLower) || shiftDateMonth.includes(searchLower);

                const matches = matchesName || matchesTime || matchesDate;

                if (filters.excludeSearchParams) {
                    if (matches) include = false; // exclude if matches
                } else {
                    if (!matches) include = false; // include only if matches
                }
            }

            // Date range filter
            if ((filters.startDate || filters.endDate) && include) {
                const shiftDate = DateTime.fromMillis(shift.start).startOf('day');
                const start = filters.startDate ? DateTime.fromISO(filters.startDate).startOf('day') : null;
                const end = filters.endDate ? DateTime.fromISO(filters.endDate).endOf('day') : null;

                const inRange = (!start || shiftDate >= start) && (!end || shiftDate <= end);

                if (filters.excludeSearchParams) {
                    if (inRange) include = false; // exclude if in range
                } else {
                    if (!inRange) include = false; // include only if in range
                }
            }

            // Day of week filter
            if (filters.days.length > 0 && include) {
                const shiftDay = DateTime.fromMillis(shift.start).toFormat('cccc').toLowerCase();
                const inDays = filters.days.includes(shiftDay);

                if (filters.excludeSearchParams) {
                    if (inDays) include = false; // exclude if in selected days
                } else {
                    if (!inDays) include = false; // include only if in selected days
                }
            }

            // Only Available filter
            if (filters.onlyAvailable && include) {
                const hasAvailableSlots = shift.slots?.some(slot => {
                    const assignedCount = shift.patrollers?.filter(p =>
                        p.role === slot.role
                    ).length || 0;
                    return assignedCount < slot.count;
                });

                if (filters.excludeSearchParams) {
                    if (hasAvailableSlots) include = false; // exclude if available
                } else {
                    if (!hasAvailableSlots) include = false; // include only if available
                }
            }

            return include;
        });
    }, [ shifts, filters ]);

    const currentPageData = React.useMemo(() => {
        const pageIndex = page - 1;

        return filteredShifts.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
    }, [ filteredShifts, page, pageSize ]);

    const paginationInfo = React.useMemo(() => {
        return { totalShifts: filteredShifts.length };
    }, [ filteredShifts.length ]);

    const saveSlots = async (id, slots) => {
        if (Array.isArray(slots) && slots.length > 0) {
            const addedSlots = await api.updateSlots({ "id": id, "slots": slots }, token);

            if (addedSlots.error) {
                throw new Error(addedSlots.error);
            }
        }
    };

    // MARK: handlers
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1); // Reset to first page when filters change
        setSelectedShifts([]); // Clear selections when filters change
    };

    const handleClearFilters = () => {
        setFilters({
            search: '',
            excludeSearchParams: false,
            days: [],
            startDate: null,
            endDate: null,
            onlyAvailable: false
        });
        setSelectedShifts([]); // Clear selections when filters are cleared
    };

    const allVisibleSelected = currentPageData.length > 0 && currentPageData.every(shift =>
        selectedShifts.some(s => s.id === shift.id)
    );

    const handleSelectAll = () => {
        if (allVisibleSelected) {
            // Deselect all visible shifts
            setSelectedShifts(prev => prev.filter(selected =>
                !currentPageData.some(visible => visible.id === selected.id)
            ));
        } else {
            // Select all visible shifts, avoiding duplicates
            setSelectedShifts(prev => {
                const newSelections = currentPageData.filter(visible =>
                    !prev.some(selected => selected.id === visible.id)
                );
                return [ ...prev, ...newSelections ];
            });
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        setSelectedShifts([]); // Clear selections when page changes
    };

    const handleRowsPerPageChange = (newPageSize) => {
        const oldPageSize = pageSize;
        const currentPageIndex = page - 1; // 0-based
        const firstVisibleItemIndex = currentPageIndex * oldPageSize;

        // Calculate which page should show the same first visible item with new page size
        const newPageIndex = Math.floor(firstVisibleItemIndex / newPageSize);
        const newPage = newPageIndex + 1; // Convert back to 1-based

        setPageSize(newPageSize);
        setPage(Math.max(1, newPage)); // Ensure we don't go below page 1
        setSelectedShifts([]); // Clear selections when page size changes
    };

    const handleNewShift = (newShift) => {
        if (newShift && newShift.target) {
            newShift = null;
        }

        // Get the date from the first shift on the current page, or use today as fallback
        const defaultDate = currentPageData.length > 0
            ? currentPageData[ 0 ].date
            : DateTime.now().toISODate();

        const shiftTemplate = newShift || {
            id: `new-${ Date.now() }`,
            name: '',
            date: defaultDate,
            start: '',
            end: '',
            slots: [],
            timezone: DateTime.now().zoneName
        };

        setNewShiftTemplate(shiftTemplate);
        setNewShiftDialogOpen(true);
    };

    const handleCopyShifts = async () => {
        if (selectedShifts.length === 1) {
            const shiftToCopy = selectedShifts[ 0 ];

            handleNewShift({ ...shiftToCopy, id: `new-${ Date.now() }`, recurrence: '' });
            setSelectedShifts([]);
        } else if (selectedShifts.length > 1) {
            setCopyDialogOpen(true);
        }
    };

    const handleSaveShift = async (shift) => {
        if (!shift) return;

        try {
            const slots = shift.slots?.map(slot => ({ "role": slot.role, "count": slot.count }));

            setLoading(true);

            if (isNewShift(shift.id)) {
                const newShift = { ...shift, event: event.id };

                if (newShift.recurrence && newShift.recurrence !== '') {
                    // Dry-run: simulate series creation for integrity check
                    const simulatedSeriesShifts = simulateSeriesCreation(newShift, event);
                    const existingShifts = shifts.filter(s => !isNewShift(s.id));

                    const integrityResult = await integrityCheckService.validateSeriesCreation(
                        simulatedSeriesShifts,
                        event,
                        existingShifts
                    );

                    if (integrityResult.hasIssues) {
                        setIntegrityResult(integrityResult);
                        setPendingShifts(simulatedSeriesShifts);
                        setPendingSeriesShift(newShift);
                        setIntegrityDialogOpen(true);
                        setLoading(false);
                        return;
                    }

                    // No issues, proceed with actual series creation
                    const seriesId = await api.createSeries(newShift, token);

                    if (seriesId.error) {
                        throw new Error(seriesId.error);
                    }

                    // get series shifts
                    const seriesShifts = await api.getShiftsBySeries(seriesId, token);

                    if (seriesShifts.error) {
                        throw new Error(seriesShifts.error);
                    }

                    // save slots for each shift in the series
                    await Promise.all(seriesShifts.map(async s => {
                        return await saveSlots(s.id, slots);
                    }));

                    setAddShifts(a => !a);

                    return;
                }

                console.log(newShift);
                const newShiftId = await api.createShift(newShift, token);
                console.log(newShiftId);
                if (newShiftId.error) {
                    throw new Error(newShiftId.error);
                }

                // Save slots if they exist
                await saveSlots(newShiftId, slots);

                // Update the shifts state with the new shift ID
                setUndo(shifts.filter(s => !isNewShift(s.id)));
                setShifts(prev => prev.map(s => s.id === shift.id ? { ...newShift, id: newShiftId } : s));
            } else if (shift.id) {
                const updatedShift = await api.updateShift(shift, token);

                if (updatedShift.error) {
                    throw new Error(updatedShift.error);
                }

                // Update slots if they exist
                await saveSlots(shift.id, slots);

                // Update the shifts state with the updated shift
                setUndo(shifts);
                setShifts(prevShifts => prevShifts.map(s => s.id === shift.id ? shift : s));
            } else {
                throw new Error('Invalid shift data');
            }
        } catch (err) {
            console.error(err);

            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveShifts = async () => {
        if (selectedShifts.length > 0) {
            try {
                setLoading(true);

                const results = await Promise.allSettled(selectedShifts.map(async shift => {
                    const response = await api.removeShift(shift.id, token);

                    return { shift, response };
                }));

                const successfulRemovals = results
                    .filter(result => result.status === 'fulfilled' && !result.value.response.error)
                    .map(result => result.value.shift);

                const failedRemovals = results
                    .filter(result => result.status === 'rejected' || result.value.response.error)
                    .map(result => result.value.shift);

                if (successfulRemovals.length > 0) {
                    setUndo(shifts);
                    setShifts(prev => prev.filter(s => !successfulRemovals.some(removed => removed.id === s.id)));
                }

                if (failedRemovals.length > 0) {
                    const failedNames = failedRemovals.map(s => s.name).join(', ');
                    setError(`Failed to remove shifts: ${ failedNames }`);
                }

                setSelectedShifts([]);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }
    };

    // Cancel a single shift (weather, etc.). Optional notify boolean will be forwarded to API
    const handleCancelShift = async (shiftId, notify = false) => {
        try {
            setLoading(true);

            const response = await api.cancelShift(shiftId, token, notify);

            if (response && response.error) {
                throw new Error(response.error);
            }

            setUndo(shifts);
            setShifts(prev => prev.filter(s => s.id !== shiftId));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmCopy = async () => {
        const newShifts = selectedShifts.map((shift, idx) => {
            const newDate = shift.date ? DateTime.fromISO(shift.date).plus({ days: copyOffset }).toISODate() : shift.date;
            const newStartTime = shift.start ? DateTime.fromMillis(shift.start).plus({ days: copyOffset }).toMillis() : shift.start;
            const newEndTime = shift.end ? DateTime.fromMillis(shift.end).plus({ days: copyOffset }).toMillis() : shift.end;

            return {
                ...shift,
                id: `preview-${ Date.now() }-${ idx }`,
                date: newDate,
                start: newStartTime,
                end: newEndTime,
                recurrence: ''
            };
        });

        // Get existing shifts for comparison (exclude currently selected ones)
        const existingShifts = shifts.filter(s => !selectedShifts.some(selected => selected.id === s.id));

        // Run integrity check
        const result = await integrityCheckService.validateShiftCopy(newShifts, event, existingShifts);

        if (result.hasIssues) {
            setIntegrityResult(result);
            setPendingShifts(newShifts);
            setIntegrityDialogOpen(true);
            return;
        }

        // No issues, proceed with creation
        await createShiftsFromPreview(newShifts);
    };

    const handleAssignPatrollers = async (assignments, initialAssignments = []) => {
        try {
            setLoading(true);

            // Determine assignments and unassignments
            const toAssign = assignments.filter(a =>
                !initialAssignments.some(ia =>
                    ia.patrollerId === a.patrollerId &&
                    ia.shiftId === a.shiftId &&
                    ia.roleId === a.roleId
                )
            );

            const toUnassign = initialAssignments.filter(ia =>
                !assignments.some(a =>
                    a.patrollerId === ia.patrollerId &&
                    a.shiftId === ia.shiftId &&
                    a.roleId === ia.roleId
                )
            );

            // Perform unassignments first
            await Promise.all(toUnassign.map(async assignment => {
                const response = await api.clearDutyAssignment(assignment.patrollerId, assignment.shiftId, token);
                
                if (response.error) {
                    throw new Error(response.error);
                }
            }));

            // Then perform assignments
            await Promise.all(toAssign.map(async assignment => {
                const response = await api.setDutyAssignment(assignment.patrollerId, assignment.shiftId, assignment.roleId, token);

                if (response.error) {
                    throw new Error(response.error);
                }
            }));

            // Refresh data
            setAddShifts(a => !a);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            setSelectedShifts([]); // Clear selections after assignment
        }
    };

    const createSeriesFromPending = async (seriesShift) => {
        try {
            setLoading(true);

            const seriesId = await api.createSeries(seriesShift, token);

            if (seriesId.error) {
                throw new Error(seriesId.error);
            }

            // get series shifts
            const seriesShifts = await api.getShiftsBySeries(seriesId, token);

            if (seriesShifts.error) {
                throw new Error(seriesShifts.error);
            }

            // save slots for each shift in the series
            const slots = seriesShift.slots?.map(slot => ({ "role": slot.role, "count": slot.count }));
            await Promise.all(seriesShifts.map(async s => {
                return await saveSlots(s.id, slots);
            }));

            setAddShifts(a => !a);
        } catch (error) {
            console.error('Error creating series:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const createShiftsFromPreview = async (previewShifts) => {
        // Convert preview IDs to real new IDs
        const newShifts = previewShifts.map((shift, idx) => ({
            ...shift,
            id: `new-${ Date.now() }-${ idx }`
        }));

        await Promise.all(newShifts.map(newShift => handleSaveShift(newShift)));

        setSelectedShifts([]);
        setAddShifts(a => !a);
        setCopyDialogOpen(false);
        setCopyOffset(0);
        setPendingShifts([]);
        setPendingSeriesShift(null);
    };

    const handleIntegrityProceed = async () => {
        setIntegrityDialogOpen(false);

        if (pendingSeriesShift) {
            // Handle series creation
            await createSeriesFromPending(pendingSeriesShift);
        } else if (pendingShifts.length > 0) {
            // Handle shift copy
            await createShiftsFromPreview(pendingShifts);
        }

        setIntegrityResult(null);
        setPendingShifts([]);
        setPendingSeriesShift(null);
    };

    const handleIntegrityCancel = () => {
        setIntegrityDialogOpen(false);
        setIntegrityResult(null);
        setPendingShifts([]);
        setPendingSeriesShift(null);
    };

    const handleCancelEditShift = () => {
        setShifts(prev => prev.filter(shift => !isNewShift(shift.id)));
        // Don't toggle addShifts - no need to reload all shifts from API
        setSelectedShifts([]);
        setUndo([]);
    };

    const handleSaveNewShift = async (newShift) => {
        try {
            setLoading(true);
            const slots = newShift.slots?.map(slot => ({ "role": slot.role, "count": slot.count }));

            if (newShift.recurrence && newShift.recurrence !== '') {
                // For recurring shifts, handleSaveShift creates the series and already flips
                // addShifts to trigger the refetch. Do not flip it again here: addShifts is a
                // boolean tick, so two flips cancel out and the list is never refreshed.
                await handleSaveShift(newShift);
            } else {
                // For single shifts, create and add to local state
                const newShiftData = { ...newShift, event: event.id };
                const newShiftId = await api.createShift(newShiftData, token);

                if (newShiftId.error) {
                    throw new Error(newShiftId.error);
                }

                // Save slots if they exist
                await saveSlots(newShiftId, slots);

                // Fetch the complete shift data with slots
                const completeShift = await api.getShift(newShiftId, token);
                
                if (completeShift.error) {
                    throw new Error(completeShift.error);
                }

                // Add the new shift to local state
                setShifts(prev => [...prev, completeShift]);
            }

            setNewShiftDialogOpen(false);
            setNewShiftTemplate(null);
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseNewShiftDialog = () => {
        setNewShiftDialogOpen(false);
        setNewShiftTemplate(null);
    };

    const handleUndo = async () => {
        if (undo && undo.length > 0) {
            try {
                setLoading(true);

                const shiftsEqual = (a, b) => a && b && a.name === b.name && a.date === b.date && a.start === b.start && a.end === b.end;

                for (const s of shifts) {
                    // in shifts, not in undo => shift added
                    const addedShift = !undo.find(u => u.id === s.id) && s;

                    // in shifts, in undo, not equal => shift updated
                    const updatedShift = undo.find(u => {
                        const s = shifts.find(s => s.id === u.id);

                        return s && !shiftsEqual(u, s);
                    });

                    if (addedShift) {
                        await api.removeShift(addedShift.id, token);
                    } else if (updatedShift) {
                        await api.updateShift(updatedShift, token);
                    }
                }

                for (const u of undo) {
                    // in undo, not in shifts => shift deleted
                    const deletedShift = !shifts.some(s => s.id === u.id) && u;

                    if (deletedShift) {
                        // do not re-create deleted shifts since it will create a new ID and break relationships;
                        // since the shift carries its old id, just restore (update) it
                        await api.updateShift(deletedShift, token);

                        const slots = deletedShift.slots?.map(slot => ({ "role": slot.role, "count": slot.count }));

                        await saveSlots(deletedShift.id, slots);
                    }
                }

                setShifts(undo);
                setUndo([]);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }
    };

    // MARK: effects
    React.useEffect(() => {
        const fetchPatrollers = async () => {
            try {
                const patrollersData = await api.getPatrollers(token);

                if (patrollersData.error) {
                    throw new Error(patrollersData.error);
                }

                setPatrollers(patrollersData);
            } catch (err) {
                console.error('Error fetching patrollers:', err);

                setPatrollers([]);
            }
        };

        fetchPatrollers();
    }, [ token ]);

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const shiftsData = await api.getShiftsByEvent(event.id, token);

                if (shiftsData.error) {
                    throw new Error(shiftsData.error);
                }

                const now = DateTime.now().toMillis();
                const dateIndex = shiftsData.findIndex(d => d.start >= now);
                const initialPage = dateIndex > 0 ? Math.floor(dateIndex / pageSize) + 1 : 1;
                const allSlots = await api.getSlots(token);

                // get associated slots for each shift
                const shiftsWithSlots = await Promise.all(shiftsData.map(async shift => {
                    const slots = allSlots.filter(slot => slot.shiftId === shift.id);

                    if (slots.error) {
                        throw new Error(slots.error);
                    }

                    return {
                        ...shift, slots: slots.map(slot => ({
                            ...slot,
                            shift: shift.id,
                            role: roles.find(role => role.id === slot.role)?.id,
                            count: slot.count || 0,
                            required: slot.required || ''
                        }))
                    };
                }));

                setShifts(shiftsWithSlots);
                setPage(initialPage);

                // Check for integrity issues in existing shifts
                await checkExistingShiftsIntegrity(shiftsWithSlots);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [ addShifts ]);

    if (loading) {
        return <LinearProgress />;
    }

    if (error) {
        return <Alert variant='filled' severity="error" onClose={ () => setError(null) }>Error loading shifts: { error }</Alert>;
    }

    // MARK: render
    return (
        <>
            <Dialog open={ copyDialogOpen } onClose={ () => {
                setCopyDialogOpen(false);
                setPendingShifts([]);
                setPendingSeriesShift(null);
            } }>
                <DialogTitle>Copy Shifts with Date Offset</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Select the number of days to add to the date of each selected shift. This will create copies of the shifts on the new dates.
                    </DialogContentText>

                    <FormControl fullWidth sx={ { mt: 2 } }>
                        <InputLabel>Days to Offset</InputLabel>

                        <Select
                            value={ copyOffset }
                            label="Days to Offset"
                            onChange={ (e) => setCopyOffset(e.target.value) }
                        >
                            { [ 0, 1, 2, 3, 4, 5, 6 ].map(day => (
                                <MenuItem key={ day } value={ day }>{ day } day{ day !== 1 ? 's' : '' }</MenuItem>
                            )) }
                        </Select>
                    </FormControl>
                </DialogContent>

                <DialogActions>
                    <Button onClick={ () => setCopyDialogOpen(false) }>Cancel</Button>
                    <Button onClick={ handleConfirmCopy } variant="contained">Copy Shifts</Button>
                </DialogActions>
            </Dialog>

            <IntegrityCheckDialog
                open={ integrityDialogOpen }
                onClose={ handleIntegrityCancel }
                result={ integrityResult }
                onProceed={ handleIntegrityProceed }
                onCancel={ handleIntegrityCancel }
                pendingShifts={ pendingShifts }
            />

            <ShiftAddDialog
                open={ newShiftDialogOpen }
                shift={ newShiftTemplate }
                roles={ roles }
                onClose={ handleCloseNewShiftDialog }
                onSave={ handleSaveNewShift }
            />

            <Filter filters={ filters } onFilterChange={ handleFilterChange } onClearFilters={ handleClearFilters } />

            <ShiftsHeader
                readOnly={ readOnly }
                count={ shifts.length }
                onNewShift={ handleNewShift }
            />

            <ShiftsList
                readOnly={ readOnly }
                shifts={ currentPageData }
                roles={ roles }
                patrollers={ memoizedPatrollers }
                selectedShifts={ selectedShifts }
                allVisibleSelected={ allVisibleSelected }
                onCheckboxChange={ setSelectedShifts }
                onSelectAll={ handleSelectAll }
                onNewShift={ handleNewShift }
                onSaveShift={ handleSaveShift }
                onCancelEditShift={ handleCancelEditShift }
                onCancelShift={ handleCancelShift }
                onAssignPatrollers={ handleAssignPatrollers }
                shiftIntegrityIssues={ shiftIntegrityIssues }
            />

            { shifts.length > 0 && <ShiftsFooter
                readOnly={ readOnly }
                paginationInfo={ paginationInfo }
                undo={ undo.length > 0 }
                rowsPerPage={ pageSize }
                onSetPage={ handlePageChange }
                onRowsPerPageChange={ handleRowsPerPageChange }
                onCopyShifts={ handleCopyShifts }
                onRemoveShifts={ handleRemoveShifts }
                onUndo={ handleUndo }
                disableCopyRemove={ selectedShifts.length === 0 }
            /> }
        </>
    );
};